import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import LoggedInHome from './pages/LoggedInHome';
import Waitlist from './pages/Waitlist';
import Login from './pages/Login';
import Register from './pages/Register';
import SearchTrips from './pages/SearchTrips';
import TripDetail from './pages/TripDetail';
import Dashboard from './pages/Dashboard';
import PostTrip from './pages/PostTrip';
import Profile from './pages/Profile';
import Chat from './pages/Chat';
import LeaveReview from './pages/LeaveReview';
import TransporterProfile from './pages/TransporterProfile';
import SenderProfile from './pages/SenderProfile';
import Admin from './pages/Admin';
import CustomsGuide from './pages/CustomsGuide';
import PostRequest from './pages/PostRequest';
import BrowseRequests from './pages/BrowseRequests';
import TripItinerary from './pages/TripItinerary';
import Terms from './pages/Terms';
import Impressum from './pages/Impressum';
import Footer from './components/Footer';
import Privacy from './pages/Privacy';
import './App.css';

// ── Coming soon flag ──────────────────────────────────────────────────────────
// Set REACT_APP_COMING_SOON=true in Vercel env vars to lock down the app.
// Set to false (or remove) when ready to launch publicly.
const COMING_SOON = process.env.REACT_APP_COMING_SOON === 'true';

// ── Gates ─────────────────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" />;
}

/**
 * ComingSoonRoute — wraps every route.
 * Allows through: admins, beta testers, and public paths.
 * Everyone else: redirect to /login (which shows normal login,
 * but they can't get further without admin/beta access).
 */
function ComingSoonRoute({ children }) {
  const { user, profile, loading } = useAuth();

  // Not in coming soon mode — let everyone through
  if (!COMING_SOON) return children;

  // Still loading auth — wait
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  // Admin or beta tester — full access
  if (user && (profile?.is_admin || profile?.is_beta_tester)) return children;

  // Not authorized — send to login
  return <Navigate to="/login" replace />;
}

/**
 * SmartHome — shows the right homepage based on auth state.
 * In coming soon mode: only admins/beta testers see the app.
 * Everyone else sees the login page.
 */
function SmartHome() {
  const { user, profile, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (COMING_SOON) {
    // Admin or beta tester — show the real app
    if (user && (profile?.is_admin || profile?.is_beta_tester)) {
      return <LoggedInHome />;
    }
    // Everyone else — show the waitlist landing page
    return <Waitlist />;
  }

  return user ? <LoggedInHome /> : <Home />;
}

function SenderRoute({ children }) {
  const { user, profile, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (profile?.role === 'transporter') return <Navigate to="/requests" />;
  return children;
}

// ── Routes ────────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <>
      <Navbar />
      <Routes>
        {/* Always public */}
        <Route path="/login"      element={<Login />} />
        <Route path="/terms"      element={<Terms />} />
        <Route path="/privacy"    element={<Privacy />} />
        <Route path="/impressum"  element={<Impressum />} />

        {/* Homepage — coming soon aware */}
        <Route path="/" element={<SmartHome />} />

        {/* All other routes — gated in coming soon mode */}
        <Route path="/register"          element={<ComingSoonRoute><Register /></ComingSoonRoute>} />
        <Route path="/search"            element={<ComingSoonRoute><SearchTrips /></ComingSoonRoute>} />
        <Route path="/trip/:id"          element={<ComingSoonRoute><TripDetail /></ComingSoonRoute>} />
        <Route path="/trip/:id/itinerary" element={<ComingSoonRoute><TripItinerary /></ComingSoonRoute>} />
        {/* Always public — even in coming soon mode */}
        <Route path="/customs"           element={<CustomsGuide />} />

        {/* Gated in coming soon mode */}
        <Route path="/requests"          element={<ComingSoonRoute><BrowseRequests /></ComingSoonRoute>} />
        <Route path="/transporter/:id"   element={<ComingSoonRoute><TransporterProfile /></ComingSoonRoute>} />
        <Route path="/sender/:id"        element={<ComingSoonRoute><SenderProfile /></ComingSoonRoute>} />

        {/* Protected + gated */}
        <Route path="/dashboard"         element={<ComingSoonRoute><ProtectedRoute><Dashboard /></ProtectedRoute></ComingSoonRoute>} />
        <Route path="/post-trip"         element={<ComingSoonRoute><ProtectedRoute><PostTrip /></ProtectedRoute></ComingSoonRoute>} />
        <Route path="/profile"           element={<ComingSoonRoute><ProtectedRoute><Profile /></ProtectedRoute></ComingSoonRoute>} />
        <Route path="/chat/:bookingId"   element={<ComingSoonRoute><ProtectedRoute><Chat /></ProtectedRoute></ComingSoonRoute>} />
        <Route path="/review/:bookingId" element={<ComingSoonRoute><ProtectedRoute><LeaveReview /></ProtectedRoute></ComingSoonRoute>} />
        <Route path="/post-request"      element={<ComingSoonRoute><SenderRoute><PostRequest /></SenderRoute></ComingSoonRoute>} />
        <Route path="/admin"             element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      </Routes>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}